import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight, MapPin, Phone, Compass, Award, Scaling, ClipboardCheck
} from "lucide-react";
import { COMPANY } from "@/lib/site";
import { SERVICES } from "@/lib/services";

const HERO_PHOTO = "/img/hero.webp";
const MAP_PHOTO = "/img/karta-seget.webp";
const WHY_ICONS = [Award, Compass, ClipboardCheck];

export default function HomePage() {
  const { t } = useTranslation();
  const stats = t("home.stats", { returnObjects: true });
  const marquee = t("home.marquee", { returnObjects: true });
  const why = t("home.why", { returnObjects: true });
  const statsArr = Array.isArray(stats) ? stats : [];
  const marqueeArr = Array.isArray(marquee) ? marquee : [];
  const whyArr = Array.isArray(why) ? why : [];

  return (
    <div data-testid="home-page">
      <Seo path="/" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-hair">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={HERO_PHOTO} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,17,33,0.95) 0%, rgba(7,17,33,0.82) 46%, rgba(7,17,33,0.45) 100%)" }} />
          <div className="absolute inset-0 grid-lines opacity-[0.08]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 pb-24 lg:pt-36 lg:pb-32">
          <div className="max-w-3xl reveal">
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold">
              {t("home.heroTitle1")}<br />
              <span className="relative inline-block">
                <span className="text-white/70">{t("home.heroTitle2")}</span>
                <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[#D92D20]" aria-hidden="true" />
              </span>
            </h1>
            <div className="mt-7 inline-flex items-center gap-2 border border-white/20 bg-white/10 backdrop-blur-sm px-3 py-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#F4A8A0]" strokeWidth={2} />
              <span className="font-brand uppercase text-[11px] tracking-[0.22em] text-white">
                {t("home.heroBadge")}
              </span>
            </div>
            <p className="text-white/80 text-lg max-w-2xl mt-8 leading-relaxed">
              {t("home.heroLead")}
              <span className="block mt-3 text-white/55">
                {t("home.heroLeadSub")}
              </span>
            </p>
          </div>
        </div>

        <div className="relative border-t border-hair bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
            {statsArr.map((s, i) => (
              <div key={s.label} className="py-7 border-r border-hair">
                <div
                  className={`font-display text-4xl sm:text-5xl tracking-tighter font-bold ${
                    i === 0 ? "text-[#D92D20]" : "text-ink"
                  }`}
                >
                  {s.kpi}
                </div>
                <div className="text-ink-soft text-xs uppercase tracking-[0.15em] mt-2">{s.label}</div>
              </div>
            ))}
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid="hero-phone-link"
              className="py-7 group hover:bg-[#FBF7F2] transition-colors"
            >
              <div className="font-display text-2xl sm:text-3xl tracking-tighter font-bold text-ink flex items-center gap-2 group-hover:text-[#D92D20] transition-colors">
                <Phone className="w-5 h-5 text-[#D92D20]" strokeWidth={1.75} />
                {COMPANY.phone}
              </div>
              <div className="text-ink-soft text-xs uppercase tracking-[0.15em] mt-2">{t("home.phoneLabel")}</div>
            </a>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative bg-ink text-white overflow-hidden border-b border-ink" aria-hidden="true">
        <div className="flex whitespace-nowrap py-4 marquee-track">
          {[...marqueeArr, ...marqueeArr].map((m, i) => (
            <div key={i} className="flex items-center gap-6 px-8">
              <span className="font-brand uppercase text-sm tracking-[0.2em] text-white/85">{m}</span>
              <span className="text-[#D92D20] text-xl leading-none">◆</span>
            </div>
          ))}
        </div>
        <style>{`
          .marquee-track { animation: marquee 40s linear infinite; }
          @keyframes marquee { to { transform: translateX(-50%); } }
          @media (prefers-reduced-motion: reduce) { .marquee-track { animation: none; } }
        `}</style>
      </section>

      {/* SERVICES GRID */}
      <section id="usluge" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 scroll-mt-20" data-testid="home-services">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="overline text-ink-soft">{t("home.servicesKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              {t("home.servicesTitle1")}<br />{t("home.servicesTitle2")}
            </h2>
          </div>
          <div className="md:col-span-5 text-ink-soft leading-relaxed">
            {t("home.servicesIntro")}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const to = s.external || `/usluge/${s.slug}`;
            return (
              <Link
                key={s.slug}
                to={to}
                className="group border border-hair p-8 bg-white relative overflow-hidden block hover-lift"
                data-testid={`service-card-${s.slug}`}
              >
                <div className={`absolute top-0 left-0 h-1 ${s.external ? "w-full bg-[#D92D20]" : "w-0 bg-[#D92D20] group-hover:w-full transition-[width] duration-500"}`} />
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 ${s.external ? "bg-[#D92D20] text-white" : "bg-ink text-white"} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <span className="font-display text-4xl text-ink/10 font-bold leading-none">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-xl font-semibold text-ink tracking-tight mt-8 leading-snug">
                  {t(`services.items.${s.slug}.title`)}
                </h3>
                <p className="text-ink-soft text-[15px] leading-relaxed mt-3">
                  {t(`services.items.${s.slug}.intro`)}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-ink text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                  {t("common.learnMore")}
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link
            to="/usluge"
            data-testid="home-all-services"
            className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors"
          >
            {t("common.allServices")}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
          <span className="text-ink-soft text-sm">{t("common.callSameDay")}</span>
        </div>
      </section>

      {/* WHY US — dark band */}
      <section className="bg-ink text-white border-t border-ink noise relative">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="overline text-[#F4A8A0]">{t("home.whyKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold mt-4 leading-[1.05] text-white">
              {t("home.whyTitle1")}<br />{t("home.whyTitle2")}
            </h2>
            <p className="text-white/75 mt-6 leading-relaxed max-w-lg">
              {t("home.whyLead")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 mt-10">
              {whyArr.map((b, i) => {
                const Icon = WHY_ICONS[i] || Award;
                return (
                  <div key={b.t} className="bg-ink p-6">
                    <Icon className="w-5 h-5 text-[#F4A8A0]" strokeWidth={1.5} />
                    <div className="font-display font-semibold text-white mt-4">{b.t}</div>
                    <div className="text-white/60 text-sm mt-1.5 leading-relaxed">{b.d}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-6 relative">
            <div className="relative aspect-[4/3] overflow-hidden border border-white/15 bg-white">
              <img
                src={MAP_PHOTO}
                alt={t("home.mapAlt")}
                className="w-full h-full object-contain"
              />
              <div className="absolute top-4 left-4 bg-white px-3 py-1.5 font-brand uppercase text-[10px] tracking-[0.2em] text-ink">
                {t("home.mapCaption")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEGALIZATION HIGHLIGHT */}
      <section className="border-t border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline text-[#D92D20]">{t("home.legalKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              {t("home.legalTitle")}
            </h2>
            <p className="text-ink-soft mt-5 leading-relaxed max-w-xl">
              {t("home.legalLead")}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/legalizacija"
                data-testid="home-legalizacija-cta"
                className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
              >
                {t("home.legalCta")}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
              <Link
                to="/prijava"
                className="inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-ink hover:text-white transition-colors"
              >
                {t("home.legalCta2")}
              </Link>
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="bg-white border border-hair p-7">
              <Scaling className="w-6 h-6 text-[#D92D20]" strokeWidth={1.5} />
              <div className="font-display text-3xl font-bold text-ink tracking-tighter mt-4">NN 48/2026</div>
              <p className="text-ink-soft text-sm mt-2 leading-relaxed">
                {t("home.legalLawNote")}
              </p>
              <Link
                to="/zakonski-propisi"
                className="inline-flex items-center gap-1.5 mt-5 text-ink text-sm font-medium border-b border-ink/30 pb-0.5 hover:border-ink"
              >
                {t("home.legalLawLink")}
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline text-[#F4A8A0]">{t("home.contactKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold mt-4 leading-[1.05]">
              {t("home.contactTitle1")}<br />{t("home.contactTitle2")}
            </h2>
            <p className="text-white/75 mt-5 leading-relaxed max-w-xl">
              {t("home.contactLead")}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/prijava"
                data-testid="home-bottom-cta"
                className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
              >
                {t("common.sendInquiry")}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
              <a
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                <Phone className="w-4 h-4" strokeWidth={1.75} />
                {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="md:col-span-4 border-t md:border-t-0 md:border-l border-white/15 md:pl-10 pt-6 md:pt-0">
            <div className="overline text-white/50">{t("common.directContact")}</div>
            <a href={`mailto:${COMPANY.email}`} className="block font-display text-xl mt-3 hover:text-white/80 break-all">
              {COMPANY.email}
            </a>
            <div className="text-white/70 text-sm mt-4 flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-white/50" strokeWidth={1.5} />
              {COMPANY.address}
            </div>
            <div className="text-white/60 text-sm mt-3">{COMPANY.hours}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
